/**
 * A custom React hook that manages the loading and replacement of props in the Unity module.
 *
 * This hook is responsible for:
 * - Fetching and loading props that are not yet ready in the Unity module
 * - Updating the database with the loaded prop data
 * - Replacing dummy objects in the Unity module with the loaded props
 *
 * The hook can be configured to listen for changes to the list of props that are not ready, and automatically fetch and load them as they become available.
 *
 * @param {boolean} [options.listenEverytime=false] - If true, the hook will continuously check for and load new props that become available.
 * @returns {object} - An object containing the `fetchAndLoadPropInUnity` and `loadProduct` functions.
 */

import { useEffect, useState } from 'react';
import { selectProductNotReady, setProductNotReady } from '../redux/slicers/admin/curatorSlicer';
import { useUnityContext } from '../components/container/unityContainer';
import { propsReadApi } from '../api/curator';
import { getPropLightList, isExclusiveProp } from '../helpers/unityHelper';
import { updateDbProp } from '../helpers/idb';
import { STATUS_DONE_KEY } from '../constants/constants';
import { useDispatch, useSelector } from 'react-redux';

export const useLoadNotReadyProduct = ({ listenEverytime = false } = {}) => {
  const dispatch = useDispatch();
  const { roomModule } = useUnityContext();
  const [isProductReady, setIsProductReady] = useState(false);
  const product = useSelector(selectProductNotReady);

  const loadProduct = async (prop = null) => {
    if (!product || !prop) return;

    const productDbData = await updateDbProp(prop);
    const lightList = await getPropLightList(prop);

    const data = {
      type: 'PropLibrary',
      id: prop.id,
      propName: prop.name,
      locationUrl: prop.is_windows_and_doors
        ? productDbData?.file_window_door || prop.product_file_window_door
        : productDbData?.file || prop.product_file,
      preDefinedMetadata: prop.product_meta_info,
      roomLightDatas: lightList,
      isExclusiveProp: isExclusiveProp(prop),
      modelType: prop?.prop_data_user_file ? 'UserImportModel' : '',
    };

    roomModule.ReplaceDummyObjectWithProp(data);

    dispatch(setProductNotReady(null));
  };

  const fetchAndLoadPropInUnity = async (propId = null) => {
    if (!propId) return;
    const prop = (await propsReadApi([propId]))?.results?.[0];
    if (
      !isProductReady &&
      product &&
      prop?.product_job_status &&
      prop?.product_job_status === STATUS_DONE_KEY
    ) {
      loadProduct(prop);
      setIsProductReady(true);
    }
  };

  useEffect(() => {
    let timeInterval;
    if (product && listenEverytime && !isProductReady) {
      timeInterval = setInterval(() => fetchAndLoadPropInUnity(product?.id), 5000);
    }

    return () => {
      if (timeInterval) clearInterval(timeInterval);
    };
  }, [product, listenEverytime, isProductReady]);

  return {
    fetchAndLoadPropInUnity,
    loadProduct,
  };
};
